import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "indicator"]

  static values = {
    decodeUrl: String
  }

  connect() {
    this.debounceTimer = null
    this.format()
  }

  disconnect() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
  }

  onInput() {
    this.format()

    // Clear existing timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }

    // Hide indicator while typing
    this.hideIndicator()

    // Debounce API calls (wait 500ms after user stops typing)
    this.debounceTimer = setTimeout(() => {
      this.attemptDecode()
    }, 500)
  }

  format() {
    const input = this.inputTarget
    const digits = input.value.replace(/\D/g, "").slice(0, 9)
    const parts = []

    if (digits.length > 0) parts.push(digits.slice(0, 3))
    if (digits.length >= 4) parts.push(digits.slice(3, 5))
    if (digits.length >= 6) parts.push(digits.slice(5, 9))

    input.value = parts.join("-")
  }

  async attemptDecode() {
    const ssn = this.inputTarget.value.replace(/\D/g, "")

    // Check if SSN matches the test pattern (999XXXYYY)
    const ssnPattern = /^999\d{6}$/
    if (!ssnPattern.test(ssn)) {
      return // Silently ignore if not a test SSN
    }

    try {
      const response = await fetch(this.decodeUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify({ ssn: ssn })
      })

      if (response.ok) {
        const data = await response.json()
        this.showSuccessIndicator(data)
      } else {
        this.hideIndicator()
      }
    } catch (error) {
      console.debug("SSN decode failed:", error)
      this.hideIndicator()
    }
  }

  showSuccessIndicator(data) {
    if (!this.hasIndicatorTarget) return

    const score = data.score
    const alerts = data.alerts || []

    let scoreClass = "text-emerald-700 bg-emerald-100"
    if (score < 580) {
      scoreClass = "text-red-700 bg-red-100"
    } else if (score < 670) {
      scoreClass = "text-amber-700 bg-amber-100"
    }

    let alertsHtml = ""
    if (alerts.length === 0) {
      alertsHtml = `<span class="text-emerald-600">No alerts</span>`
    } else {
      const alertCodes = alerts.map(a => a.code).join(", ")
      alertsHtml = `<span class="text-red-600">Alerts: ${alertCodes}</span>`
    }

    this.indicatorTarget.hidden = false
    this.indicatorTarget.innerHTML = `
      <div class="mt-1 flex flex-wrap items-center gap-2 text-xs">
        <span class="inline-flex items-center rounded-full px-2 py-1 font-semibold ${scoreClass}">
          <svg class="mr-1 h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
          </svg>
          Score: ${score}
        </span>
        ${alertsHtml}
      </div>
    `
  }

  hideIndicator() {
    if (this.hasIndicatorTarget) {
      this.indicatorTarget.hidden = true
      this.indicatorTarget.innerHTML = ""
    }
  }

  csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta && meta.getAttribute("content")
  }
}
